import { ClassValue } from 'clsx';
import { UserImage } from '@shared/components/Image';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { PersonPill, Pill } from '@shared/components/pill';
import { PersonField } from '@shared/data/entity/fields/PersonField';
import { User } from '@shared/types/user';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { getPersonText } from './util/getPersonText';
import { getStackItems } from './util/getStackItems';
import styles from './PersonCollectionViewField.module.css';

const MAX_PEOPLE_TO_SHOW = 3;
const MAX_PILL_WIDTH = 180;
/** Slots in the compact avatar stack; past that, the last slot is a "+N". */
const PEOPLE_STACK_SLOTS = 3;

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: PersonField;
}

/**
 * Default: a pill per person, up to three, and a "+N". Compact: overlapping avatars
 * (photos, or initials without one), up to three, or two and a "+N" circle. Inline: the first
 * person's name and a "+N".
 */
export function PersonCollectionViewField({ field, variant, className }: Readonly<Props>) {
  if (field.isEmpty) {
    return null;
  }

  const isInline = variant === 'inline';
  const isCompact = variant === 'compact';
  const isDefault = variant === 'default';

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={[styles.personCollectionViewField, className]}
    >
      {isInline && <InlinePerson field={field} />}
      {isCompact && <CompactPerson field={field} />}
      {isDefault && <DefaultPerson field={field} />}
    </CollectionViewFieldContainer>
  );
}

function InlinePerson({ field }: { field: PersonField }) {
  return <>{getPersonText(field)}</>;
}

function DefaultPerson({ field }: { field: PersonField }) {
  const peopleToShow: User[] = field.users.slice(0, MAX_PEOPLE_TO_SHOW);
  const remainingPersonCount = field.users.length - MAX_PEOPLE_TO_SHOW;
  return (
    <>
      {peopleToShow.map(user => (
        <PersonPill key={user.id} user={user} maxWidth={MAX_PILL_WIDTH} showLastName={false} />
      ))}
      {remainingPersonCount > 0 && (
        <Pill backgroundColor="white" sections={[`+${remainingPersonCount}`]} round noBorder />
      )}
    </>
  );
}

function CompactPerson({ field }: { field: PersonField }) {
  const { shownItems: shownUsers, moreCount } = getStackItems(field.users, PEOPLE_STACK_SLOTS);
  return (
    <View row centerVertically className={styles.people}>
      {shownUsers.map(user => (
        <UserImage key={user.id} user={user} size="small" className={styles.avatar} />
      ))}
      {moreCount > 0 && (
        <View center className={[styles.avatar, styles.morePeople]}>
          <Text size="caption1" weight="semibold" inactive>{`+${moreCount}`}</Text>
        </View>
      )}
    </View>
  );
}
